import {useTranslation} from '@aboutyou/i18n';
import {
  useTrackFeature,
  useTrackFeatureInViewImpression,
} from '@aboutyou/tracking';
import {Button, COLORS, GenericIconName, Icon} from '@aboutyou/ui-components';
import {styled} from '@linaria/react';
import React from 'react';

const TRACK_FEATURE_LABEL = 'addToBasket_actionButton';

export function AddToBasketActionButton({
  isAddToBasketInView,
  isLoading,
  onClick,
}: {
  isAddToBasketInView: boolean;
  isLoading: boolean;
  onClick: () => void;
}) {
  const {t} = useTranslation();
  const {ref} = useTrackFeatureInViewImpression({label: TRACK_FEATURE_LABEL});
  const trackFeature = useTrackFeature();

  const handleClick = () => {
    trackFeature({eventType: 'click', label: TRACK_FEATURE_LABEL});
    onClick();
  };

  return (
    <Wrapper
      ref={ref as React.RefObject<HTMLDivElement | null>}
      $hidden={isAddToBasketInView}
    >
      <BasketButton
        data-testid="addToBasket"
        $hidden={isAddToBasketInView}
        variant="primary"
        isLoading={isLoading}
        onClick={handleClick}
        aria-label={t('Add to basket')}
        aria-hidden={isAddToBasketInView}
        tabIndex={isAddToBasketInView ? -1 : 0}
        size="sm"
        round={true}
      >
        <Icon
          fill="currentColor"
          size="S"
          asset={GenericIconName.AddToBasket}
        />
      </BasketButton>
    </Wrapper>
  );
}

const BasketButton = styled(Button)<{$hidden: boolean}>`
  && {
    /* opacity/transition must live at doubled specificity: the base Button
       declares opacity at single-class specificity, and when style chunks are
       injected one at a time (dev server) whichever equal-specificity rule
       lands last wins, flashing the hidden button visible. */
    transition: opacity 0.1s ease-in-out, background-color 300ms ease-in-out,
      box-shadow 300ms ease-in-out;
    opacity: ${({$hidden}) => ($hidden ? 0 : 1)};
    --background-color: ${COLORS['Fill.Black']};
    --color: ${COLORS['Text.White']};
  }
`;

const Wrapper = styled.div<{$hidden: boolean}>`
  pointer-events: ${({$hidden}) => ($hidden ? 'none' : 'auto')};
`;
